// Section templates with default content and settings
export interface SectionTemplate {
  type: string;
  labelAr: string;
  labelEn: string;
  icon: string;
  descriptionAr: string;
  descriptionEn: string;
  defaultSettings: Record<string, any>;
  defaultContent: Record<string, any>;
  settingsSchema: SettingField[];
}

export interface SettingField {
  key: string;
  labelAr: string;
  labelEn: string;
  type: 'text' | 'textarea' | 'number' | 'switch' | 'select' | 'color' | 'image';
  options?: { value: string; labelAr: string; labelEn: string }[];
  defaultValue?: any;
}

export const SECTION_TEMPLATES: SectionTemplate[] = [
  // ========== HERO SECTION ==========
  {
    type: 'hero',
    labelAr: 'البانر الرئيسي',
    labelEn: 'Hero Banner',
    icon: 'Image',
    descriptionAr: 'قسم ترحيبي كبير مع صورة ونص ورابط',
    descriptionEn: 'Large welcome section with image, text and CTA',
    defaultSettings: {
      showBadge: true,
      badgeTextAr: '🎉 عروض حصرية',
      badgeTextEn: '🎉 Exclusive Offers',
      showWave: true,
      layout: 'image-right', // image-right, image-left, centered, fullwidth
    },
    defaultContent: {
      titleAr: 'تسوق أفضل المنتجات',
      titleEn: 'Shop the Best Products',
      subtitleAr: 'اكتشف مجموعتنا المميزة من المنتجات عالية الجودة بأسعار منافسة',
      subtitleEn: 'Discover our premium collection of high-quality products at competitive prices',
      buttonTextAr: 'تسوق الآن',
      buttonTextEn: 'Shop Now',
      buttonLink: '/products',
      backgroundImage: '',
    },
    settingsSchema: [
      { key: 'showBadge', labelAr: 'إظهار شارة العروض', labelEn: 'Show Offers Badge', type: 'switch' },
      { key: 'badgeTextAr', labelAr: 'نص الشارة (عربي)', labelEn: 'Badge Text (Arabic)', type: 'text' },
      { key: 'badgeTextEn', labelAr: 'نص الشارة (إنجليزي)', labelEn: 'Badge Text (English)', type: 'text' },
      { key: 'showWave', labelAr: 'إظهار موجة الفاصل', labelEn: 'Show Wave Divider', type: 'switch' },
      { 
        key: 'layout', 
        labelAr: 'تخطيط القسم', 
        labelEn: 'Section Layout', 
        type: 'select',
        options: [
          { value: 'image-right', labelAr: 'صورة يمين', labelEn: 'Image Right' },
          { value: 'image-left', labelAr: 'صورة يسار', labelEn: 'Image Left' },
          { value: 'centered', labelAr: 'نص في المنتصف', labelEn: 'Centered Text' },
        ]
      },
    ],
  },

  // ========== FEATURES SECTION ==========
  {
    type: 'features',
    labelAr: 'مميزات المتجر',
    labelEn: 'Store Features',
    icon: 'CheckCircle',
    descriptionAr: 'عرض مميزات المتجر مثل الشحن والضمان والدعم',
    descriptionEn: 'Display store features like shipping, warranty, support',
    defaultSettings: {
      columns: 3,
      showIcons: true,
      style: 'default', // default, cards, minimal
    },
    defaultContent: {
      features: [
        { iconName: 'Truck', titleAr: 'شحن سريع', titleEn: 'Fast Shipping', descAr: 'توصيل لجميع المحافظات', descEn: 'Delivery to all governorates' },
        { iconName: 'Shield', titleAr: 'ضمان الجودة', titleEn: 'Quality Guarantee', descAr: 'استرجاع خلال 14 يوم', descEn: '14 days return policy' },
        { iconName: 'Headphones', titleAr: 'دعم 24/7', titleEn: '24/7 Support', descAr: 'خدمة عملاء متميزة', descEn: 'Outstanding service' },
      ],
    },
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 3 },
      { key: 'showIcons', labelAr: 'إظهار الأيقونات', labelEn: 'Show Icons', type: 'switch' },
      { 
        key: 'style', 
        labelAr: 'نمط العرض', 
        labelEn: 'Display Style', 
        type: 'select',
        options: [
          { value: 'default', labelAr: 'افتراضي', labelEn: 'Default' },
          { value: 'cards', labelAr: 'بطاقات', labelEn: 'Cards' },
          { value: 'minimal', labelAr: 'بسيط', labelEn: 'Minimal' },
        ]
      },
    ],
  },

  // ========== CATEGORIES SECTION ==========
  {
    type: 'categories',
    labelAr: 'الأقسام',
    labelEn: 'Categories',
    icon: 'Grid',
    descriptionAr: 'عرض أقسام المتجر بصور جذابة',
    descriptionEn: 'Display store categories with attractive images',
    defaultSettings: {
      columns: 5,
      showAllLink: true,
      style: 'cards', // cards, grid, list
      limit: 10,
    },
    defaultContent: {},
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة (شاشة كبيرة)', labelEn: 'Columns (Desktop)', type: 'number', defaultValue: 5 },
      { key: 'limit', labelAr: 'الحد الأقصى للعرض', labelEn: 'Display Limit', type: 'number', defaultValue: 10 },
      { key: 'showAllLink', labelAr: 'إظهار رابط عرض الكل', labelEn: 'Show View All Link', type: 'switch' },
    ],
  },

  // ========== FEATURED PRODUCTS ==========
  {
    type: 'featured_products',
    labelAr: 'منتجات مميزة',
    labelEn: 'Featured Products',
    icon: 'Star',
    descriptionAr: 'عرض المنتجات المميزة التي تختارها',
    descriptionEn: 'Display your selected featured products',
    defaultSettings: {
      columns: 4,
      limit: 8,
      showViewAll: true,
    },
    defaultContent: {},
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 4 },
      { key: 'limit', labelAr: 'عدد المنتجات', labelEn: 'Products Count', type: 'number', defaultValue: 8 },
      { key: 'showViewAll', labelAr: 'إظهار زر عرض الكل', labelEn: 'Show View All Button', type: 'switch' },
    ],
  },

  // ========== BEST SELLERS ==========
  {
    type: 'best_sellers',
    labelAr: 'الأكثر مبيعاً',
    labelEn: 'Best Sellers',
    icon: 'Flame',
    descriptionAr: 'يعرض المنتجات الأكثر مبيعاً تلقائياً',
    descriptionEn: 'Automatically displays best-selling products',
    defaultSettings: {
      columns: 4,
      limit: 8,
      showBadge: true,
    },
    defaultContent: {},
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 4 },
      { key: 'limit', labelAr: 'عدد المنتجات', labelEn: 'Products Count', type: 'number', defaultValue: 8 },
      { key: 'showBadge', labelAr: 'إظهار شارة "تلقائي"', labelEn: 'Show Auto Badge', type: 'switch' },
    ],
  },

  // ========== BANNER SECTION ==========
  {
    type: 'banner',
    labelAr: 'بانر إعلاني',
    labelEn: 'Promotional Banner',
    icon: 'Megaphone',
    descriptionAr: 'بانر ملفت للعروض والتخفيضات',
    descriptionEn: 'Eye-catching banner for offers and discounts',
    defaultSettings: {
      style: 'gradient', // gradient, image, solid
      backgroundColor: '#10b981',
      textColor: '#ffffff',
    },
    defaultContent: {
      titleAr: 'خصم حصري 30%',
      titleEn: 'Exclusive 30% Off',
      subtitleAr: 'استخدم كود SAVE30 واحصل على خصم فوري',
      subtitleEn: 'Use code SAVE30 and get instant discount',
      buttonTextAr: 'تسوق الآن',
      buttonTextEn: 'Shop Now',
      buttonLink: '/products',
      couponCode: 'SAVE30',
      backgroundImage: '',
    },
    settingsSchema: [
      { 
        key: 'style', 
        labelAr: 'نمط الخلفية', 
        labelEn: 'Background Style', 
        type: 'select',
        options: [
          { value: 'gradient', labelAr: 'تدرج لوني', labelEn: 'Gradient' },
          { value: 'image', labelAr: 'صورة', labelEn: 'Image' },
          { value: 'solid', labelAr: 'لون واحد', labelEn: 'Solid Color' },
        ]
      },
      { key: 'backgroundColor', labelAr: 'لون الخلفية', labelEn: 'Background Color', type: 'color' },
    ],
  },

  // ========== COUNTDOWN ==========
  {
    type: 'countdown',
    labelAr: 'عداد تنازلي',
    labelEn: 'Countdown Timer',
    icon: 'Clock',
    descriptionAr: 'عداد تنازلي للعروض المحدودة',
    descriptionEn: 'Countdown timer for limited offers',
    defaultSettings: {
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      style: 'boxes', // boxes, inline, circle
    },
    defaultContent: {
      titleAr: 'العرض ينتهي خلال',
      titleEn: 'Offer Ends In',
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      buttonTextAr: 'اشتري الآن',
      buttonTextEn: 'Buy Now',
      buttonLink: '/products',
    },
    settingsSchema: [
      { key: 'showDays', labelAr: 'إظهار الأيام', labelEn: 'Show Days', type: 'switch' },
      { key: 'showHours', labelAr: 'إظهار الساعات', labelEn: 'Show Hours', type: 'switch' },
      { key: 'showMinutes', labelAr: 'إظهار الدقائق', labelEn: 'Show Minutes', type: 'switch' },
      { key: 'showSeconds', labelAr: 'إظهار الثواني', labelEn: 'Show Seconds', type: 'switch' },
      { 
        key: 'style', 
        labelAr: 'نمط العداد', 
        labelEn: 'Timer Style', 
        type: 'select',
        options: [
          { value: 'boxes', labelAr: 'مربعات', labelEn: 'Boxes' },
          { value: 'inline', labelAr: 'سطر واحد', labelEn: 'Inline' },
          { value: 'circle', labelAr: 'دوائر', labelEn: 'Circles' },
        ]
      },
    ],
  },

  // ========== TESTIMONIALS ==========
  {
    type: 'testimonials',
    labelAr: 'آراء العملاء',
    labelEn: 'Customer Reviews',
    icon: 'Quote',
    descriptionAr: 'عرض تقييمات وآراء العملاء',
    descriptionEn: 'Display customer reviews and testimonials',
    defaultSettings: {
      columns: 3,
      autoplay: true,
      showRating: true,
    },
    defaultContent: {
      testimonials: [
        { name: 'أحمد محمد', nameEn: 'Ahmed Mohamed', rating: 5, textAr: 'خدمة ممتازة وتوصيل سريع، أنصح الجميع بالتعامل معهم', textEn: 'Excellent service and fast delivery' },
        { name: 'سارة أحمد', nameEn: 'Sara Ahmed', rating: 5, textAr: 'منتجات عالية الجودة وأسعار مناسبة جداً', textEn: 'High quality products and great prices' },
        { name: 'محمود علي', nameEn: 'Mahmoud Ali', rating: 4, textAr: 'تجربة شراء رائعة، سأكرر التعامل بالتأكيد', textEn: 'Great shopping experience' },
      ],
    },
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 3 },
      { key: 'autoplay', labelAr: 'تشغيل تلقائي', labelEn: 'Autoplay', type: 'switch' },
      { key: 'showRating', labelAr: 'إظهار التقييم', labelEn: 'Show Rating', type: 'switch' },
    ],
  },

  // ========== NEWSLETTER ==========
  {
    type: 'newsletter',
    labelAr: 'النشرة البريدية',
    labelEn: 'Newsletter',
    icon: 'Mail',
    descriptionAr: 'نموذج اشتراك في النشرة البريدية',
    descriptionEn: 'Newsletter subscription form',
    defaultSettings: {
      style: 'centered', // centered, split, minimal
      showIcon: true,
    },
    defaultContent: {
      titleAr: 'اشترك في نشرتنا البريدية',
      titleEn: 'Subscribe to Our Newsletter',
      subtitleAr: 'احصل على أحدث العروض والمنتجات الجديدة',
      subtitleEn: 'Get the latest offers and new products',
      buttonTextAr: 'اشترك',
      buttonTextEn: 'Subscribe',
      placeholderAr: 'أدخل بريدك الإلكتروني',
      placeholderEn: 'Enter your email',
    },
    settingsSchema: [
      { 
        key: 'style', 
        labelAr: 'نمط العرض', 
        labelEn: 'Display Style', 
        type: 'select',
        options: [
          { value: 'centered', labelAr: 'في المنتصف', labelEn: 'Centered' },
          { value: 'split', labelAr: 'مقسم', labelEn: 'Split' },
          { value: 'minimal', labelAr: 'بسيط', labelEn: 'Minimal' },
        ]
      },
      { key: 'showIcon', labelAr: 'إظهار الأيقونة', labelEn: 'Show Icon', type: 'switch' },
    ],
  },

  // ========== PRODUCT GRID ==========
  {
    type: 'product_grid',
    labelAr: 'شبكة منتجات',
    labelEn: 'Product Grid',
    icon: 'LayoutGrid',
    descriptionAr: 'عرض منتجات من قسم معين أو الكل',
    descriptionEn: 'Display products from specific category or all',
    defaultSettings: {
      columns: 4,
      limit: 8,
      source: 'all', // all, category, manual
      categoryId: null,
      sortBy: 'newest', // newest, price_low, price_high, popular
    },
    defaultContent: {},
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 4 },
      { key: 'limit', labelAr: 'عدد المنتجات', labelEn: 'Products Count', type: 'number', defaultValue: 8 },
      { 
        key: 'sortBy', 
        labelAr: 'ترتيب حسب', 
        labelEn: 'Sort By', 
        type: 'select',
        options: [
          { value: 'newest', labelAr: 'الأحدث', labelEn: 'Newest' },
          { value: 'price_low', labelAr: 'السعر: الأقل', labelEn: 'Price: Low to High' },
          { value: 'price_high', labelAr: 'السعر: الأعلى', labelEn: 'Price: High to Low' },
          { value: 'popular', labelAr: 'الأكثر شعبية', labelEn: 'Most Popular' },
        ]
      },
    ],
  },

  // ========== SLIDER ==========
  {
    type: 'slider',
    labelAr: 'سلايدر صور',
    labelEn: 'Image Slider',
    icon: 'Layers',
    descriptionAr: 'سلايدر صور متحرك مع روابط',
    descriptionEn: 'Animated image slider with links',
    defaultSettings: {
      autoplay: true,
      interval: 5000,
      showDots: true,
      showArrows: true,
    },
    defaultContent: {
      slides: [
        { imageUrl: '', titleAr: 'عرض خاص', titleEn: 'Special Offer', link: '/products' },
        { imageUrl: '', titleAr: 'منتجات جديدة', titleEn: 'New Products', link: '/products' },
      ],
    },
    settingsSchema: [
      { key: 'autoplay', labelAr: 'تشغيل تلقائي', labelEn: 'Autoplay', type: 'switch' },
      { key: 'interval', labelAr: 'الفاصل الزمني (مللي ثانية)', labelEn: 'Interval (ms)', type: 'number', defaultValue: 5000 },
      { key: 'showDots', labelAr: 'إظهار النقاط', labelEn: 'Show Dots', type: 'switch' },
      { key: 'showArrows', labelAr: 'إظهار الأسهم', labelEn: 'Show Arrows', type: 'switch' },
    ],
  },

  // ========== SPACER ==========
  {
    type: 'spacer',
    labelAr: 'مسافة فارغة',
    labelEn: 'Spacer',
    icon: 'Space',
    descriptionAr: 'إضافة مسافة فارغة بين الأقسام',
    descriptionEn: 'Add empty space between sections',
    defaultSettings: {
      height: 60,
      showDivider: false,
    },
    defaultContent: {},
    settingsSchema: [
      { key: 'height', labelAr: 'الارتفاع (بكسل)', labelEn: 'Height (px)', type: 'number', defaultValue: 60 },
      { key: 'showDivider', labelAr: 'إظهار خط فاصل', labelEn: 'Show Divider Line', type: 'switch' },
    ],
  },

  // ========== CUSTOM HTML ==========
  {
    type: 'custom_html',
    labelAr: 'HTML مخصص',
    labelEn: 'Custom HTML',
    icon: 'Code',
    descriptionAr: 'إضافة كود HTML مخصص',
    descriptionEn: 'Add custom HTML code',
    defaultSettings: {
      fullWidth: false,
    },
    defaultContent: {
      html: '',
    },
    settingsSchema: [
      { key: 'fullWidth', labelAr: 'عرض كامل', labelEn: 'Full Width', type: 'switch' },
    ],
  },

  // ========== BRANDS/PARTNERS ==========
  {
    type: 'brands',
    labelAr: 'العلامات التجارية',
    labelEn: 'Brands / Partners',
    icon: 'Award',
    descriptionAr: 'عرض شعارات العلامات التجارية والشركاء',
    descriptionEn: 'Display brand logos and partners',
    defaultSettings: {
      autoScroll: true,
      grayscale: true,
      columns: 6,
    },
    defaultContent: {
      brands: [
        { name: 'Brand 1', logoUrl: '' },
        { name: 'Brand 2', logoUrl: '' },
      ],
    },
    settingsSchema: [
      { key: 'autoScroll', labelAr: 'تمرير تلقائي', labelEn: 'Auto Scroll', type: 'switch' },
      { key: 'grayscale', labelAr: 'تدرج رمادي', labelEn: 'Grayscale', type: 'switch' },
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 6 },
    ],
  },

  // ========== VIDEO SECTION ==========
  {
    type: 'video',
    labelAr: 'قسم فيديو',
    labelEn: 'Video Section',
    icon: 'Play',
    descriptionAr: 'عرض فيديو من يوتيوب أو فيميو',
    descriptionEn: 'Display video from YouTube or Vimeo',
    defaultSettings: {
      autoplay: false,
      showControls: true,
      aspectRatio: '16:9',
    },
    defaultContent: {
      titleAr: 'شاهد الفيديو',
      titleEn: 'Watch Video',
      videoUrl: '',
      thumbnailUrl: '',
    },
    settingsSchema: [
      { key: 'autoplay', labelAr: 'تشغيل تلقائي', labelEn: 'Autoplay', type: 'switch' },
      { key: 'showControls', labelAr: 'إظهار أزرار التحكم', labelEn: 'Show Controls', type: 'switch' },
      { 
        key: 'aspectRatio', 
        labelAr: 'نسبة الأبعاد', 
        labelEn: 'Aspect Ratio', 
        type: 'select',
        options: [
          { value: '16:9', labelAr: '16:9', labelEn: '16:9' },
          { value: '4:3', labelAr: '4:3', labelEn: '4:3' },
          { value: '1:1', labelAr: 'مربع', labelEn: 'Square' },
        ]
      },
    ],
  },

  // ========== FAQ SECTION ==========
  {
    type: 'faq',
    labelAr: 'الأسئلة الشائعة',
    labelEn: 'FAQ',
    icon: 'HelpCircle',
    descriptionAr: 'قسم الأسئلة والأجوبة الشائعة',
    descriptionEn: 'Frequently asked questions section',
    defaultSettings: {
      expandFirst: true,
      allowMultiple: false,
    },
    defaultContent: {
      faqs: [
        { questionAr: 'كيف يمكنني تتبع طلبي؟', questionEn: 'How can I track my order?', answerAr: 'يمكنك تتبع طلبك من خلال رابط التتبع المرسل على هاتفك', answerEn: 'You can track your order via the tracking link sent to your phone' },
        { questionAr: 'ما هي سياسة الإرجاع؟', questionEn: 'What is the return policy?', answerAr: 'يمكنك إرجاع المنتج خلال 14 يوم من الاستلام', answerEn: 'You can return the product within 14 days of receipt' },
      ],
    },
    settingsSchema: [
      { key: 'expandFirst', labelAr: 'فتح السؤال الأول', labelEn: 'Expand First Question', type: 'switch' },
      { key: 'allowMultiple', labelAr: 'فتح أكثر من سؤال', labelEn: 'Allow Multiple Open', type: 'switch' },
    ],
  },

  // ========== STATS SECTION ==========
  {
    type: 'stats',
    labelAr: 'إحصائيات',
    labelEn: 'Statistics',
    icon: 'BarChart3',
    descriptionAr: 'عرض إحصائيات المتجر بأرقام جذابة',
    descriptionEn: 'Display store statistics with attractive numbers',
    defaultSettings: {
      columns: 4,
      animate: true,
      style: 'default',
    },
    defaultContent: {
      stats: [
        { valueAr: '10,000+', valueEn: '10,000+', labelAr: 'عميل سعيد', labelEn: 'Happy Customers', icon: 'Users' },
        { valueAr: '5,000+', valueEn: '5,000+', labelAr: 'منتج', labelEn: 'Products', icon: 'Package' },
        { valueAr: '27', valueEn: '27', labelAr: 'محافظة', labelEn: 'Governorates', icon: 'MapPin' },
        { valueAr: '24/7', valueEn: '24/7', labelAr: 'دعم متواصل', labelEn: 'Support', icon: 'Headphones' },
      ],
    },
    settingsSchema: [
      { key: 'columns', labelAr: 'عدد الأعمدة', labelEn: 'Columns', type: 'number', defaultValue: 4 },
      { key: 'animate', labelAr: 'تحريك الأرقام', labelEn: 'Animate Numbers', type: 'switch' },
      { 
        key: 'style', 
        labelAr: 'نمط العرض', 
        labelEn: 'Display Style', 
        type: 'select',
        options: [
          { value: 'default', labelAr: 'افتراضي', labelEn: 'Default' },
          { value: 'cards', labelAr: 'بطاقات', labelEn: 'Cards' },
          { value: 'gradient', labelAr: 'خلفية متدرجة', labelEn: 'Gradient Background' },
        ]
      },
    ],
  },

  // ========== CTA SECTION ==========
  {
    type: 'cta',
    labelAr: 'دعوة للإجراء',
    labelEn: 'Call to Action',
    icon: 'Megaphone',
    descriptionAr: 'قسم دعوة قوي مع زر إجراء',
    descriptionEn: 'Strong call-to-action section with action button',
    defaultSettings: {
      style: 'gradient',
      showIcon: true,
    },
    defaultContent: {
      titleAr: 'لا تفوت العروض الحصرية',
      titleEn: "Don't Miss Exclusive Offers",
      subtitleAr: 'سجل الآن واحصل على خصم 10% على طلبك الأول',
      subtitleEn: 'Sign up now and get 10% off your first order',
      buttonTextAr: 'سجل الآن',
      buttonTextEn: 'Sign Up Now',
      buttonLink: '/auth',
    },
    settingsSchema: [
      { 
        key: 'style', 
        labelAr: 'نمط الخلفية', 
        labelEn: 'Background Style', 
        type: 'select',
        options: [
          { value: 'gradient', labelAr: 'تدرج لوني', labelEn: 'Gradient' },
          { value: 'solid', labelAr: 'لون واحد', labelEn: 'Solid Color' },
          { value: 'image', labelAr: 'صورة', labelEn: 'Image' },
        ]
      },
      { key: 'showIcon', labelAr: 'إظهار أيقونة', labelEn: 'Show Icon', type: 'switch' },
    ],
  },


  // ========== IMAGE WITH TEXT ==========
  {
    type: 'image_text',
    labelAr: 'صورة مع نص',
    labelEn: 'Image with Text',
    icon: 'LayoutPanelLeft',
    descriptionAr: 'قسم مقسم نصفين: صورة ونص',
    descriptionEn: 'Split section: image and text',
    defaultSettings: {
      imagePosition: 'left',
      contentAlign: 'center',
    },
    defaultContent: {
      titleAr: 'لماذا تختارنا؟',
      titleEn: 'Why Choose Us?',
      textAr: 'نقدم لكم أفضل المنتجات بأعلى جودة وأسعار منافسة',
      textEn: 'We offer the best products with highest quality and competitive prices',
      imageUrl: '',
      buttonTextAr: 'اكتشف المزيد',
      buttonTextEn: 'Discover More',
      buttonLink: '/about',
    },
    settingsSchema: [
      { 
        key: 'imagePosition', 
        labelAr: 'موضع الصورة', 
        labelEn: 'Image Position', 
        type: 'select',
        options: [
          { value: 'left', labelAr: 'يسار', labelEn: 'Left' },
          { value: 'right', labelAr: 'يمين', labelEn: 'Right' },
        ]
      },
      { 
        key: 'contentAlign', 
        labelAr: 'محاذاة المحتوى', 
        labelEn: 'Content Alignment', 
        type: 'select',
        options: [
          { value: 'top', labelAr: 'أعلى', labelEn: 'Top' },
          { value: 'center', labelAr: 'وسط', labelEn: 'Center' },
          { value: 'bottom', labelAr: 'أسفل', labelEn: 'Bottom' },
        ]
      },
    ],
  },

  // ========== COLLECTION HIGHLIGHT ==========
  {
    type: 'collection',
    labelAr: 'مجموعة مميزة',
    labelEn: 'Collection Highlight',
    icon: 'Sparkles',
    descriptionAr: 'تسليط الضوء على مجموعة منتجات معينة',
    descriptionEn: 'Highlight a specific product collection',
    defaultSettings: {
      layout: 'grid',
      showPrice: true,
      limit: 4,
    },
    defaultContent: {
      titleAr: 'مجموعة الصيف',
      titleEn: 'Summer Collection',
      subtitleAr: 'اكتشف أحدث صيحات الموسم',
      subtitleEn: 'Discover the latest trends of the season',
      categoryId: null,
    },
    settingsSchema: [
      { 
        key: 'layout', 
        labelAr: 'تخطيط', 
        labelEn: 'Layout', 
        type: 'select',
        options: [
          { value: 'grid', labelAr: 'شبكة', labelEn: 'Grid' },
          { value: 'carousel', labelAr: 'سلايدر', labelEn: 'Carousel' },
        ]
      },
      { key: 'showPrice', labelAr: 'إظهار السعر', labelEn: 'Show Price', type: 'switch' },
      { key: 'limit', labelAr: 'عدد المنتجات', labelEn: 'Product Count', type: 'number', defaultValue: 4 },
    ],
  },
];

export function getSectionTemplate(type: string): SectionTemplate | undefined {
  return SECTION_TEMPLATES.find(t => t.type === type);
}
